/**
 * ScoreGauge — compact radial gauge for a 0–100 score (talent ↔ job fit metrics).
 *
 * Design: a single thin ring on a neutral track with the score centered and a small
 * caption beneath. Two on-brand tones distinguish the metrics without color soup:
 *   - tone="match" → crisp dark ring (--color-dark-invirtus), the primary metric
 *   - tone="fit"   → brand lilac ring (--color-purple), the secondary metric
 * No gradients. Respects prefers-reduced-motion. Right-aligned in the assigned-jobs row.
 */

import React from 'react';

export type ScoreGaugeTone = 'match' | 'fit';

export interface ScoreGaugeProps {
  /** Score 0–100, or null/undefined when not scored (renders "-") */
  value?: number | null;
  /** Caption under the ring (e.g. "Match" / "Fit") */
  label: string;
  tone?: ScoreGaugeTone;
  /** Outer diameter in px (default 44) */
  size?: number;
}

const ARC_COLOR: Record<ScoreGaugeTone, string> = {
  match: 'var(--color-dark-invirtus)',
  fit: 'var(--color-purple)',
};

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({
  value,
  label,
  tone = 'match',
  size = 44,
}) => {
  const hasScore = value !== undefined && value !== null && !Number.isNaN(value);
  const clamped = hasScore ? Math.max(0, Math.min(100, Math.round(value as number))) : 0;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - clamped / 100);
  const display = hasScore ? `${clamped}%` : '-';

  return (
    <div
      className="flex shrink-0 flex-col items-center gap-1"
      role="img"
      aria-label={`${label} ${display}`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          // Inline, so container rules that size nested icons (e.g.
          // DropdownMenu.Item's `[&_svg]:size-4`) can't shrink the gauge.
          style={{ width: size, height: size }}
          className="-rotate-90"
          aria-hidden="true"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--color-dark-invirtus-soft)"
            strokeWidth={stroke}
          />
          {hasScore && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={ARC_COLOR[tone]}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none"
            />
          )}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums text-dark-invirtus">
          {display}
        </span>
      </div>
      <span className="text-[10px] font-medium uppercase tracking-wide text-dark-invirtus/55">
        {label}
      </span>
    </div>
  );
};

ScoreGauge.displayName = 'ScoreGauge';
